/**
 * CRDT REST operations issued by the canvas whiteboard.
 *
 * All calls go through authFetch so an expired access-token triggers a
 * refresh + one retry instead of failing silently mid-session (the
 * previous inline fetch + getAccessToken pattern had no retry).
 */

import { CONTENT_API } from '../config'
import { authFetch } from './authFetch'
import { uploadFile } from './storage'
import { createEdge, EdgeConflictError } from './edges'

const JSON_HEADERS = { 'Content-Type': 'application/json' }

function applyUrl(postId: number): string {
  return `${CONTENT_API}/api/v1/posts/${postId}/crdt/apply`
}

async function fetchStructured(postId: number): Promise<{
  paragraphs: Array<{
    index: number
    is_heading?: boolean
    block_id?: string | null
    block_type?: string | null
  }>
} | null> {
  const res = await authFetch(
    `${CONTENT_API}/api/v1/posts/${postId}/crdt/structured`,
    { headers: JSON_HEADERS },
  ).catch(() => null)
  if (!res || !res.ok) return null
  return res.json()
}

/**
 * Create a new card at a world position. Two-step because the server
 * rejects `canvas_meta` alongside `mode='append'`: append a placeholder
 * paragraph first, then best-effort stamp canvas_meta on it via
 * update_paragraph_attrs. If the follow-up fails the card still exists
 * and lands at the masonry-stagger fallback position.
 */
export async function createCardAt(
  postId: number,
  worldX: number,
  worldY: number,
): Promise<void> {
  const appendRes = await authFetch(applyUrl(postId), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ mode: 'append', text: 'Neue Kachel' }),
  }).catch(() => null)
  if (!appendRes || !appendRes.ok) return

  try {
    const data = await fetchStructured(postId)
    if (!data) return
    const lastNonHeading = [...data.paragraphs]
      .reverse()
      .find((p) => !p.is_heading)
    if (lastNonHeading == null) return
    await authFetch(applyUrl(postId), {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        mode: 'update_paragraph_attrs',
        paragraph_index: lastNonHeading.index,
        canvas_meta: {
          x: Math.round(worldX),
          y: Math.round(worldY),
          width: 280,
          height: 120,
        },
      }),
    })
  } catch {
    // Position follow-up failed — card appears via stagger fallback.
  }
}

/**
 * Delete one or more paragraphs. Indices are deleted highest-first so
 * earlier deletions don't shift the remaining targets — this is what
 * lets a merged multi-paragraph card (one commit) delete atomically
 * from the user's perspective.
 */
export async function deleteParagraphs(
  postId: number,
  paragraphIndices: number[],
): Promise<void> {
  const sorted = [...paragraphIndices].sort((a, b) => b - a)
  for (const idx of sorted) {
    await authFetch(applyUrl(postId), {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        mode: 'delete_paragraph',
        paragraph_index: idx,
      }),
    }).catch(() => undefined)
  }
}

/**
 * Upload a dropped image to permanent public storage and insert it as
 * a `media` paragraph right after the source card, plus a `references`
 * edge back to the source so the canvas draws the Bezier connection.
 *
 * Insert-after (not replace) is deliberate: replacing would overwrite
 * the source card's text with just the image markdown.
 */
export async function insertImageAfter(
  postId: number,
  sourceParagraphIndex: number,
  sourceBlockId: string | null,
  file: File,
): Promise<void> {
  const { url } = await uploadFile(file, {
    isPublic: true,
    permanent: true,
    context: `post-${postId}-paragraph-${sourceParagraphIndex}`,
  })
  const altText = file.name.replace(/\.[^.]+$/, '') || 'image'
  const insertRes = await authFetch(applyUrl(postId), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      mode: 'insert_after_paragraph',
      paragraph_index: sourceParagraphIndex,
      text: `![${altText}](${url})`,
      block_type: 'media',
    }),
  })
  if (!insertRes.ok || !sourceBlockId) return

  const data = await fetchStructured(postId)
  if (!data) return
  const mediaBlock = data.paragraphs.find(
    (p) =>
      p.index === sourceParagraphIndex + 1 && p.block_type === 'media',
  )
  if (!mediaBlock?.block_id) return
  try {
    await createEdge(postId, {
      from_block_id: mediaBlock.block_id,
      to_block_id: sourceBlockId,
      edge_type: 'references',
      label: 'media dropped on card',
      confidence: null,
    })
  } catch (err) {
    if (!(err instanceof EdgeConflictError)) throw err
  }
}
